import { Button } from '@/components/ui/button'
import { Drawer, DrawerContent, DrawerOverlay } from '@/components/ui/drawer'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { Separator } from '@/components/ui/separator'
import client from '@/services/client.service'
import { normalizeUrl } from '@/lib/url'
import { useCurrentPubkey } from '@/atoms/session'
import { isSmallScreenAtom } from '@/atoms/screenSize'
import { HugeiconsIcon } from '@hugeicons/react'
import { CheckIcon, FolderAddIcon, AddCircleIcon, Loading03Icon } from '@hugeicons/core-free-icons'
import { useAtomValue } from 'jotai'
import { invalidateFavorites, favoritesChannelAtom } from '@/atoms/invalidation'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import DrawerMenuItem from './DrawerMenuItem'
import { useAsyncRetry } from 'react-use'
import { toast } from 'sonner'
import { fetchFavoriteRelaysWithSets, loadFavoriteRelays } from '@nostr/gadgets/lists'
import * as kinds from '@nostr/tools/kinds'
import { now } from '@/lib/utils'
import { getReplaceableCoordinateFromEvent } from '@/lib/event'
import { EventTemplate } from '@nostr/tools/core'
import { ResolvedSet } from '@nostr/gadgets/sets'

export default function SaveRelayDropdownMenu({
  item,
  bigButton = false
}: {
  item: string
  bigButton?: boolean
}) {
  const isSmallScreen = useAtomValue(isSmallScreenAtom)
  const pubkey = useCurrentPubkey()
  const channel = useAtomValue(favoritesChannelAtom)
  const { value: favorites } = useAsyncRetry(
    async () => (pubkey ? fetchFavoriteRelaysWithSets(pubkey) : []),
    [pubkey, channel]
  )

  const relaySets = (favorites || []).filter(
    (f): f is ResolvedSet<string> => typeof f !== 'string'
  ) as ResolvedSet<string>[]

  const relayUrl = normalizeUrl(item)

  const directlyFavorited = useMemo(() => {
    if (!favorites) return false
    return favorites.filter((f) => typeof f === 'string').includes(relayUrl)
  }, [favorites, relayUrl])

  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [favoriting, setFavoriting] = useState(false)
  const { t } = useTranslation()

  const trigger = bigButton ? (
    <Button variant="ghost" size="titlebar-icon" onClick={() => setIsDrawerOpen(true)}>
      <HugeiconsIcon icon={FolderAddIcon} />
    </Button>
  ) : (
    <button
      className="enabled:hover:text-primary [&_svg]:size-5 pr-0 pt-0.5"
      onClick={(e) => {
        e.stopPropagation()
        setIsDrawerOpen(true)
      }}
    >
      <HugeiconsIcon icon={FolderAddIcon} />
    </button>
  )

  async function handleFavoriteClick() {
    if (!pubkey) return
    if (favoriting) return

    setFavoriting(true)
    try {
      const fr = await loadFavoriteRelays(pubkey, [], true)
      const tags = fr.event
        ? fr.event.tags.filter((t) => !(t[0] === 'relay' && t[1] === relayUrl))
        : []

      if (!directlyFavorited) tags.push(['relay', relayUrl])

      const event = await client.publish({
        kind: kinds.FavoriteRelays,
        content: '',
        tags,
        created_at: now()
      })

      if (event) {
        await loadFavoriteRelays(event.pubkey, undefined, event)
        invalidateFavorites()
      } else {
        console.warn('failed to publish updated favorite relays event')
        toast.error(t('Failed to publish updated favorite relays event.'))
      }
    } finally {
      setFavoriting(false)
    }
  }

  const favoriteIcon = favoriting ? (
    <HugeiconsIcon icon={Loading03Icon} className="animate-spin" />
  ) : directlyFavorited ? (
    <HugeiconsIcon icon={CheckIcon} />
  ) : (
    <HugeiconsIcon icon={AddCircleIcon} />
  )

  const relayContent = (
    <>
      {isSmallScreen ? (
        <DrawerMenuItem onClick={handleFavoriteClick} disabled={favoriting}>
          {favoriteIcon}
          {directlyFavorited ? t('Unfavorite') : t('Favorite')}
        </DrawerMenuItem>
      ) : (
        <DropdownMenuItem
          className="flex gap-2"
          onSelect={(e) => e.preventDefault()}
          onClick={handleFavoriteClick}
          disabled={favoriting}
        >
          {favoriteIcon}
          {directlyFavorited ? t('Unfavorite') : t('Favorite')}
        </DropdownMenuItem>
      )}
      {relaySets.map((set) => (
        <RelaySetItem key={set.pointer.identifier} set={set} url={relayUrl} />
      ))}
      {isSmallScreen ? <Separator /> : <DropdownMenuSeparator />}
      <SaveToNewSet url={relayUrl} />
    </>
  )

  if (isSmallScreen) {
    return (
      <div>
        {trigger}
        <div onClick={(e) => e.stopPropagation()}>
          <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
            <DrawerOverlay onClick={() => setIsDrawerOpen(false)} />
            <DrawerContent hideOverlay>
              <div className="py-2">{relayContent}</div>
            </DrawerContent>
          </Drawer>
        </div>
      </div>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild className="px-2">
        {trigger}
      </DropdownMenuTrigger>
      <DropdownMenuContent onClick={(e) => e.stopPropagation()}>{relayContent}</DropdownMenuContent>
    </DropdownMenu>
  )
}

function RelaySetItem({ set, url }: { set: ResolvedSet<string>; url: string }) {
  const { t } = useTranslation()
  const isSmallScreen = useAtomValue(isSmallScreenAtom)
  const pubkey = useCurrentPubkey()
  const [saving, setSaving] = useState(false)
  const saved = set.items.includes(url)

  const icon = saving ? (
    <HugeiconsIcon icon={Loading03Icon} className="animate-spin" />
  ) : saved ? (
    <HugeiconsIcon icon={CheckIcon} />
  ) : (
    <HugeiconsIcon icon={AddCircleIcon} />
  )

  if (isSmallScreen) {
    return (
      <DrawerMenuItem onClick={handleClick} disabled={saving}>
        {icon}
        {set.title}
      </DrawerMenuItem>
    )
  }

  return (
    <DropdownMenuItem
      key={set.pointer.identifier}
      className="flex gap-2"
      onSelect={(e) => e.preventDefault()}
      onClick={handleClick}
      disabled={saving}
    >
      {icon}
      {set.title}
    </DropdownMenuItem>
  )

  async function handleClick() {
    if (!pubkey) return
    if (saving) return

    setSaving(true)
    try {
      const updatedUrls = saved ? set.items.filter((u) => u !== url) : [...set.items, url]

      const event = await client.publish({
        kind: kinds.Relaysets,
        content: '',
        tags: [
          ['d', set.pointer.identifier],
          ...(set.title && set.title !== set.pointer.identifier ? [['title', set.title]] : []),
          ...updatedUrls.map((url) => ['relay', url])
        ],
        created_at: now()
      })
      if (event) {
        client.addEventToCache(event)
        invalidateFavorites()
      } else {
        console.warn('failed to publish updated relay set event')
        toast.error(t('Failed to publish updated relay set event.'))
      }
    } finally {
      setSaving(false)
    }
  }
}

function SaveToNewSet({ url }: { url: string }) {
  const { t } = useTranslation()
  const isSmallScreen = useAtomValue(isSmallScreenAtom)
  const pubkey = useCurrentPubkey()
  const [saving, setSaving] = useState(false)

  const icon = saving ? (
    <HugeiconsIcon icon={Loading03Icon} className="animate-spin" />
  ) : (
    <HugeiconsIcon icon={FolderAddIcon} />
  )

  if (isSmallScreen) {
    return (
      <DrawerMenuItem onClick={handleSave} disabled={saving}>
        {icon}
        {t('Save to a new relay set')}
      </DrawerMenuItem>
    )
  }

  return (
    <DropdownMenuItem onSelect={(e) => e.preventDefault()} onClick={handleSave} disabled={saving}>
      {icon}
      {t('Save to a new relay set')}
    </DropdownMenuItem>
  )

  async function handleSave() {
    if (!pubkey) return
    if (saving) return

    const favoriteRelaysP = loadFavoriteRelays(pubkey, [], true)
    const newSetName = prompt(t('Enter a name for the new relay set'))
    if (!newSetName) return

    setSaving(true)
    try {
      const newSet: ResolvedSet<string> = {
        pointer: { identifier: Date.now().toString(36), pubkey, kind: kinds.Relaysets },
        title: newSetName,
        items: [url],
        event: null
      }
      const newSetEvent = await client.publish({
        kind: kinds.Relaysets,
        content: '',
        tags: [
          ['d', newSet.pointer.identifier],
          ['title', newSet.title],
          ...newSet.items.map((url) => ['relay', url])
        ],
        created_at: now()
      })
      if (!newSetEvent) {
        console.warn('failed to publish relay set event')
        toast.error(t('Failed to publish relay set event.'))
        return
      }
      newSet.event = newSetEvent
      client.addEventToCache(newSetEvent)

      const fr = await favoriteRelaysP
      const draft: EventTemplate = {
        kind: kinds.FavoriteRelays,
        content: '',
        tags: [],
        ...(fr.event || {}),
        created_at: now()
      }
      draft.tags.push(['a', getReplaceableCoordinateFromEvent(newSetEvent)])

      const event = await client.publish(draft)
      if (event) {
        await loadFavoriteRelays(event.pubkey, undefined, event)
        invalidateFavorites()
      } else {
        console.warn('failed to publish updated favorite relays event')
        toast.error(t('Failed to publish updated favorite relays event.'))
      }
    } finally {
      setSaving(false)
    }
  }
}
